import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useTenantContext } from '../../context/TenantContext';

// Icônes utilisées dans le contenu
import { Code, ShieldCheck, Target, Anchor, ArrowRight, Wrench, Server, Globe } from 'lucide-react';

import * as LucideIcons from 'lucide-react';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
    const cleaned = hex.replace('#', '');
    const bigint = parseInt(cleaned.length === 3
        ? cleaned.split('').map((c) => c + c).join('')
        : cleaned, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Composant réutilisable pour les cartes de valeurs/actions avec style SIS
const InfoCard = ({ icon, title, text, delay, color }: { icon: React.ReactNode; title: string; text: string; delay: number; color: string }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
        className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border text-center group hover:scale-105 transition-all duration-500"
        style={{ borderColor: `${color}33` }}
    >
        <div className="w-16 h-16 mx-auto mb-4" style={{ color }}>
            {icon}
        </div>
        <h3 className="text-xl font-bold mb-2 group-hover:text-white transition-colors duration-300" style={{ color: 'white' }}>{title}</h3>
        <p className="text-white/80 group-hover:text-white/90 transition-colors duration-300">{text}</p>
    </motion.div>
);

export const AboutPage: React.FC = () => {
    const { tenant, previewConfig } = useTenantContext();

    // Thème About (preview > tenant)
    const aboutTheme = React.useMemo(() => {
        const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
        const themeColors = {
            c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
            c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
            c3: (tenant?.config?.color3 as string) || '#ffffff',
            c4: (tenant?.config?.color4 as string) || '#9ca3af',
        };
        const defaults = {
            overlay: {
                enabled: true,
                useGradient: true,
                orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
                color1: '#08062b',
                color2: '#08062b',
                color3: '#08062b',
                opacity1: 0.95,
                opacity2: 0.80,
                opacity3: 0.95,
            },
            icons: [
                { enabled: true, icon: 'Wrench',   color: themeColors.c1, size: 32 }, // top-left
                { enabled: true, icon: 'Server',   color: themeColors.c2, size: 28 }, // top-right
                { enabled: true, icon: 'Code',     color: themeColors.c2, size: 24 }, // bottom-left
                { enabled: true, icon: 'Award',    color: themeColors.c1, size: 30 }, // bottom-right
            ]
        } as any;
        return {
            overlay: {
                enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
                useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
                orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
                color1: base.overlay?.color1 ?? defaults.overlay.color1,
                color2: base.overlay?.color2 ?? defaults.overlay.color2,
                color3: base.overlay?.color3 ?? defaults.overlay.color3,
                opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
                opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
                opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
            },
            icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
                ? base.icons.map((i: any, idx: number) => ({
                    enabled: i?.enabled ?? defaults.icons[idx].enabled,
                    icon: i?.icon ?? defaults.icons[idx].icon,
                    color: i?.color ?? defaults.icons[idx].color,
                    size: Number(i?.size ?? defaults.icons[idx].size),
                }))
                : defaults.icons,
        };
    }, [tenant, previewConfig]);
    
    return (
        <PublicLayout>
            <SEOMetaTags 
                title="À propos - Solution Informatique Services (SIS)"
                description="Découvrez l'histoire, la mission et les valeurs de SIS, votre partenaire technologique au Congo pour des solutions informatiques fiables et innovantes."
                url="https://www.sis-cg.com/a-propos"
            />

            <div className="min-h-screen relative overflow-hidden">
                {/* Fond avec la nouvelle palette de couleurs SIS */}
                <div 
                    className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
                    style={{
                        backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
                        backgroundAttachment: 'fixed'
                    }}
                >
                    {aboutTheme.overlay.enabled && (
                        <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                            backgroundImage: aboutTheme.overlay.useGradient
                                ? `linear-gradient(${(
                                    {
                                        'to-b': 'to bottom',
                                        'to-t': 'to top',
                                        'to-r': 'to right',
                                        'to-l': 'to left',
                                        'to-tr': 'to top right',
                                        'to-br': 'to bottom right',
                                        'to-tl': 'to top left',
                                        'to-bl': 'to bottom left',
                                    } as Record<string, string>
                                )[aboutTheme.overlay.orientation] || 'to bottom'},
                                    ${hexToRgba(aboutTheme.overlay.color1, aboutTheme.overlay.opacity1)},
                                    ${hexToRgba(aboutTheme.overlay.color2, aboutTheme.overlay.opacity2)},
                                    ${hexToRgba(aboutTheme.overlay.color3, aboutTheme.overlay.opacity3)}
                                )`
                                : undefined,
                            backgroundColor: !aboutTheme.overlay.useGradient
                                ? hexToRgba(aboutTheme.overlay.color1, aboutTheme.overlay.opacity2)
                                : undefined,
                        }} />
                    )}
                </div>

                {/* Particules flottantes avec couleurs et icônes personnalisables */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {/* Top-Left */}
                    {aboutTheme.icons[0]?.enabled && (
                        <motion.div
                            className="absolute top-20 left-10"
                            style={{ color: hexToRgba(aboutTheme.icons[0].color, 0.2) }}
                            animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
                            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                        >
                            {(() => {
                              const IconComponent = iconMap[aboutTheme.icons[0].icon] || iconMap.Wrench;
                              return IconComponent ? <IconComponent size={aboutTheme.icons[0].size} /> : null;
                            })()}
                        </motion.div>
                    )}
                    {/* Top-Right */}
                    {aboutTheme.icons[1]?.enabled && (
                        <motion.div
                            className="absolute top-40 right-20"
                            style={{ color: hexToRgba(aboutTheme.icons[1].color, 0.2) }}
                            animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
                            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                        >
                            {(() => {
                              const IconComponent = iconMap[aboutTheme.icons[1].icon] || iconMap.Server;
                              return IconComponent ? <IconComponent size={aboutTheme.icons[1].size} /> : null;
                            })()}
                        </motion.div>
                    )}
                    {/* Bottom-Left */}
                    {aboutTheme.icons[2]?.enabled && (
                        <motion.div
                            className="absolute bottom-40 left-20"
                            style={{ color: hexToRgba(aboutTheme.icons[2].color, 0.2) }}
                            animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
                            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                        >
                            {(() => {
                              const IconComponent = iconMap[aboutTheme.icons[2].icon] || iconMap.Code;
                              return IconComponent ? <IconComponent size={aboutTheme.icons[2].size} /> : null;
                            })()}
                        </motion.div>
                    )}
                    {/* Bottom-Right */}
                    {aboutTheme.icons[3]?.enabled && (
                        <motion.div
                            className="absolute bottom-20 right-10"
                            style={{ color: hexToRgba(aboutTheme.icons[3].color, 0.2) }}
                            animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
                            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
                        >
                            {(() => {
                              const IconComponent = iconMap[aboutTheme.icons[3].icon] || iconMap.Award;
                              return IconComponent ? <IconComponent size={aboutTheme.icons[3].size} /> : null;
                            })()}
                        </motion.div>
                    )}
                </div>

                {/* Contenu de la page */}
                <div className="relative z-10">
                    {/* --- Section Héros --- */}
                    <section className="min-h-screen flex items-center justify-center text-center relative py-20">
                        <div className="container mx-auto px-4 relative z-10">
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                                className="max-w-4xl mx-auto"
                            >
                                <motion.div 
                                    initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
                                    className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 mb-8 shadow-xl"
                                    style={{ borderColor: `${tenant?.config?.color2 || '#008aef'}4d` }}
                                >
                                    <Code className="w-5 h-5 mr-3 animate-pulse" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                                    <span className="font-semibold text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Notre histoire</span>
                                </motion.div>
                                
                                <motion.h1 
                                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
                                    className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-2xl"
                                >
                                    Votre partenaire technologique au <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Congo</span>
                                </motion.h1>
                                
                                <motion.p 
                                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
                                    className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed"
                                >
                                    Depuis nos débuts, nous nous engageons à fournir des solutions informatiques fiables, sécurisées et innovantes pour accompagner les entreprises congolaises dans leur transformation numérique.
                                </motion.p>
                                
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.8 }}
                                    className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                                >
                                    <a href="/contact" className="font-bold py-4 px-8 rounded-xl text-lg inline-flex items-center transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-xl"
                                        style={{ 
                                            backgroundColor: tenant?.config?.color2 || '#008aef',
                                            color: tenant?.config?.color3 || '#ffffff'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#008aef'}dd`;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = tenant?.config?.color2 || '#008aef';
                                        }}
                                    >
                                        <ShieldCheck className="w-5 h-5 mr-3" />
                                        Nous contacter
                                    </a>
                                    <a href="#valeurs" className="font-bold py-4 px-8 rounded-xl text-lg inline-flex items-center transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-xl"
                                        style={{ 
                                            backgroundColor: tenant?.config?.color1 || '#fe8d17',
                                            color: tenant?.config?.color3 || '#ffffff'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}dd`;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                                        }}
                                    >
                                        <ArrowRight className="w-5 h-5 mr-3" />
                                        Nos valeurs
                                    </a>
                                </motion.div>
                            </motion.div>
                        </div>
                    </section>

                    {/* --- Section Histoire & Identité --- */}
                    <section className="py-20">
                        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
                            <motion.div 
                                initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }} transition={{ duration: 0.7 }}
                            >
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                    Notre <span style={{ color: tenant?.config?.color2 || '#008aef' }}>Histoire</span> et <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Identité</span>
                                </h2>
                                <p className="text-white/80 leading-relaxed mb-6">
                                    Fondé au Congo, SIS est né de la volonté de combler le fossé entre les besoins technologiques des entreprises locales et les solutions disponibles sur le marché. 
                                    Notre équipe d'experts passionnés s'engage à fournir des services informatiques de qualité supérieure, adaptés aux réalités du marché congolais.
                                </p>
                                <h3 className="text-2xl font-bold mb-3" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Notre Mission</h3>
                                <p className="text-white/80 leading-relaxed mb-6">
                                    Accompagner les entreprises congolaises dans leur transformation numérique en leur fournissant des solutions informatiques fiables, 
                                    sécurisées et innovantes qui soutiennent leur croissance et leur compétitivité.
                                </p>
                                <h3 className="text-2xl font-bold mb-3" style={{ color: tenant?.config?.color2 || '#008aef' }}>Notre Vision</h3>
                                <p className="text-white/80 leading-relaxed">
                                    Devenir le leader incontournable des services informatiques au Congo, reconnu pour notre excellence technique, 
                                    notre engagement envers la communauté et notre capacité à transformer les défis technologiques en opportunités.
                                </p>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }} transition={{ duration: 0.7 }}
                                className="rounded-2xl overflow-hidden shadow-2xl"
                            >
                                <img 
                                    src="https://image.api.sportal365.com/process/smp-images-production/pulse.ci/05092024/2b4a77e2-5159-40b4-a044-4c4730a58ac7?operations=autocrop(520:330)" 
                                    alt="Équipe SIS en réunion stratégique" 
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>
                        </div>
                    </section>

                    {/* --- Section Valeurs --- */}
                    <section id="valeurs" className="py-20 bg-white/5">
                        <div className="container mx-auto px-4 text-center">
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} 
                                viewport={{ once: true }} transition={{ duration: 0.7 }}
                                className="mb-12"
                            >
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                    Les <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Piliers</span> de Notre Engagement
                                </h2>
                                <p className="text-white/80 text-lg max-w-3xl mx-auto">
                                    Nos valeurs fondamentales guident chaque aspect de notre travail et définissent qui nous sommes en tant qu'entreprise.
                                </p>
                            </motion.div>
                            
                            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                                <InfoCard 
                                    delay={0} 
                                    color={tenant?.config?.color1 || '#fe8d17'}
                                    icon={<ShieldCheck className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Intégrité" 
                                    text="Nous agissons avec honnêteté et transparence dans toutes nos interactions, garantissant la confiance de nos clients." 
                                />
                                <InfoCard 
                                    delay={0.2} 
                                    color={tenant?.config?.color2 || '#008aef'}
                                    icon={<Target className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Excellence" 
                                    text="Nous nous engageons à fournir des solutions de la plus haute qualité, dépassant constamment les attentes de nos clients." 
                                />
                                <InfoCard 
                                    delay={0.4} 
                                    color={tenant?.config?.color1 || '#fe8d17'}
                                    icon={<Anchor className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Engagement" 
                                    text="Nous nous investissons pleinement dans la réussite de nos clients et dans le développement de notre communauté." 
                                />
                            </div>
                        </div>
                    </section>
                    
                    {/* --- Section Domaines d'Expertise --- */}
                    <section className="py-20">
                        <div className="container mx-auto px-4 text-center">
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} 
                                viewport={{ once: true }} transition={{ duration: 0.7 }}
                                className="mb-12"
                            >
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                    Nos <span style={{ color: tenant?.config?.color2 || '#008aef' }}>Domaines</span> d'<span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Expertise</span>
                                </h2>
                                <p className="text-white/80 text-lg max-w-3xl mx-auto">
                                    Nous maîtrisons un large éventail de technologies et de domaines pour répondre à tous vos besoins informatiques.
                                </p>
                            </motion.div>
                            
                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                                <InfoCard 
                                    delay={0} 
                                    color={tenant?.config?.color1 || '#fe8d17'}
                                    icon={<Wrench className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Support & Maintenance" 
                                    text="Dépannage rapide, maintenance préventive et optimisation de vos systèmes informatiques." 
                                />
                                <InfoCard 
                                    delay={0.1} 
                                    color={tenant?.config?.color2 || '#008aef'}
                                    icon={<Server className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Réseaux & Infrastructure" 
                                    text="Conception, déploiement et gestion d'infrastructures réseau sécurisées et évolutives." 
                                />
                                <InfoCard 
                                    delay={0.2} 
                                    color={tenant?.config?.color1 || '#fe8d17'}
                                    icon={<Code className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Développement Web" 
                                    text="Création de sites web modernes et d'applications sur mesure pour votre présence digitale." 
                                />
                                <InfoCard 
                                    delay={0.3} 
                                    color={tenant?.config?.color2 || '#008aef'}
                                    icon={<Globe className="w-full h-full" strokeWidth={1.5}/>} 
                                    title="Solutions Cloud" 
                                    text="Migration vers le cloud, gestion des données et solutions de stockage sécurisées." 
                                />
                            </div>
                        </div>
                    </section>
                    
                    {/* --- CTA vers la page Contact --- */}
                    <section className="py-20 bg-white/5">
                        <div className="container mx-auto px-4 text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                    Prêt à transformer votre entreprise avec <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>SIS</span> ?
                                </h2>
                                <p className="text-white/80 max-w-2xl mx-auto mb-8 text-lg">
                                    Contactez-nous dès aujourd'hui pour discuter de vos besoins informatiques et découvrir comment nous pouvons vous aider à atteindre vos objectifs.
                                </p>
                                <a 
                                   href="/contact"
                                   className="font-bold py-4 px-8 rounded-xl text-lg inline-flex items-center transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-xl"
                                   style={{ 
                                       backgroundColor: tenant?.config?.color1 || '#fe8d17',
                                       color: tenant?.config?.color3 || '#ffffff'
                                   }}
                                   onMouseEnter={(e) => {
                                       e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}dd`;
                                   }}
                                   onMouseLeave={(e) => {
                                       e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                                   }}
                                >
                                    <span>Demander un devis</span>
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </a>
                            </motion.div>
                        </div>
                    </section>
                </div>
            </div>
        </PublicLayout>
    );
};

export default AboutPage;